import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, Pencil, Sheet as SheetIcon, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/Field";
import { ReportTable } from "@/components/ReportTable";
import { SignatureBlock } from "@/components/SignatureBlock";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { exportPDF, exportXLSX, withShowing } from "@/lib/exporters";
import { fmtDate, money } from "@/lib/format";
import { buildConference, conferenceSections } from "@/lib/conference";
import { eventTotals } from "@/lib/registrations";
import { RecordDialog } from "@/components/RecordDialog";
import { RECORD_CONFIGS } from "@/lib/records";

function PayCell({ p, perm, onEdit, onDelete }) {
  return (
    <>
      {money(p.amount)}<div className="text-[11px] font-sans text-slate-500">{fmtDate(p.date)}</div>
      {(perm.edit || perm.del) && (
        <div className="no-print mt-1 flex justify-end gap-1">
          {perm.edit && <button type="button" onClick={() => onEdit(p)} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Edit payment" data-testid={`conf-pay-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
          {perm.del && <button type="button" onClick={() => onDelete(p)} className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-700" aria-label="Delete payment" data-testid={`conf-pay-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      )}
    </>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${accent || ""}`} data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function GoalCell({ row, canEdit, onSave }) {
  const [v, setV] = useState(String(row.goal));
  useEffect(() => { setV(String(row.goal)); }, [row.goal]);
  if (!canEdit) return <span className="font-mono">{money(row.goal)}</span>;
  const commit = () => { const n = Number(v); if (v !== "" && !Number.isNaN(n) && n !== row.goal) onSave(row.church.id, n); else setV(String(row.goal)); };
  return (
    <Input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className={`h-8 w-28 bg-white text-right font-mono text-slate-900 ${row.custom ? "border-amber-500" : ""}`} title={row.custom ? "Custom goal for this church" : "Default goal"} data-testid={`conf-goal-${row.church.id}`} />
  );
}

function PaymentsTable({ m, canEdit, onGoal, perm, onEditPay, onDeletePay }) {
  const td = "px-3 py-2 whitespace-nowrap";
  const num = `${td} text-right font-mono`;
  const span = m.cols + 4;
  return (
    <div className="report-section space-y-2" data-testid="conf-payments">
      <h3 className="font-heading text-lg font-bold">Church payments</h3>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="report-table w-full text-sm">
          <thead><tr className="bg-[hsl(var(--primary))] text-left text-white">
            <th className={td}>Church</th><th className={`${td} text-right`}>Goal</th>
            {Array.from({ length: m.cols }, (_, i) => <th key={i} className={`${td} text-right`}>Payment {i + 1}</th>)}
            <th className={`${td} text-right`}>Total paid</th><th className={`${td} text-right`}>Remaining</th>
          </tr></thead>
          <tbody>
            {m.groups.map((g) => [
              <tr key={`h-${g.key}`} className="bg-[hsl(var(--sand))]"><td colSpan={span} className={`${td} font-heading font-bold text-[hsl(var(--primary))]`} data-testid={`conf-region-${g.key}`}>{g.name}</td></tr>,
              ...g.rows.map((r) => (
                <tr key={r.church.id} className="border-t border-slate-100" data-testid={`conf-row-${r.church.id}`}>
                  <td className={`${td} font-semibold`}>{r.church.name}</td>
                  <td className={`${td} text-right`}><GoalCell row={r} canEdit={canEdit} onSave={onGoal} /></td>
                  {Array.from({ length: m.cols }, (_, i) => <td key={i} className={num}>{r.pays[i] ? <PayCell p={r.pays[i]} perm={perm} onEdit={onEditPay} onDelete={onDeletePay} /> : ""}</td>)}
                  <td className={`${num} font-bold`} data-testid={`conf-paid-${r.church.id}`}>{money(r.paid)}</td>
                  <td className={`${num} ${r.remaining === 0 && r.goal > 0 ? "text-emerald-700" : ""}`} data-testid={`conf-remaining-${r.church.id}`}>{money(r.remaining)}</td>
                </tr>
              )),
              <tr key={`s-${g.key}`} className="border-t border-slate-300 bg-amber-50/70 font-bold" data-testid={`conf-subtotal-${g.key}`}>
                <td className={td}>{g.name} sub-total</td><td className={num}>{money(g.goal)}</td><td colSpan={m.cols} />
                <td className={num}>{money(g.paid)}</td><td className={num}>{money(g.remaining)}</td>
              </tr>,
            ])}
            {!m.groups.length && <tr><td colSpan={span} className="px-3 py-6 text-center text-slate-500">No churches yet.</td></tr>}
          </tbody>
          <tfoot><tr className="border-t-2 border-slate-400 bg-amber-100 font-bold" data-testid="conf-payments-total">
            <td className={td}>TOTAL</td><td className={num}>{money(m.t.goal)}</td><td colSpan={m.cols} /><td className={num}>{money(m.t.paid)}</td><td className={num}>{money(m.t.remaining)}</td>
          </tr></tfoot>
        </table>
      </div>
    </div>
  );
}

function DefaultGoal({ col, canEdit, onSave }) {
  const [v, setV] = useState(String(col?.goal ?? ""));
  useEffect(() => { setV(String(col?.goal ?? "")); }, [col?.id, col?.goal]);
  if (!canEdit) return null;
  return (
    <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); onSave(v === "" ? null : Number(v)); }}>
      <div><div className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">Default goal per church</div>
        <Input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} className="h-10 w-36 bg-white text-slate-900" data-testid="conf-default-goal" /></div>
      <Button type="submit" variant="outline" className="h-10" data-testid="conf-default-goal-save">Save</Button>
    </form>
  );
}

function ViewTabs({ detail, setDetail }) {
  return (
    <div className="inline-flex rounded-full border border-slate-300 bg-white p-1" data-testid="conf-view-tabs">
      {[[false, "Summary Only"], [true, "Full Detail"]].map(([d, l]) => (
        <button key={l} type="button" onClick={() => setDetail(d)} data-testid={`conf-view-${d ? "detail" : "summary"}`}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${detail === d ? "bg-[hsl(var(--primary))] text-white" : "text-slate-700 hover:bg-slate-100"}`}>{l}</button>
      ))}
    </div>
  );
}

export function ConferenceSummary({ filters, sig, section, only = "all", showing = "" }) {
  const ctx = useApp();
  const cols = ctx.options.collections;
  const [name, setName] = useState(filters.collection || cols.find((c) => c.goal > 0)?.name || cols[0]?.name || "");
  const [detail, setDetail] = useState(true);
  const [data, setData] = useState({ donations: [], expenses: [], registrations: [] });
  const rawCol = cols.find((c) => c.name === name);
  const secKey = section === "General Council" ? "gc" : section === "MWJK" ? "mwjk" : "";
  const col = useMemo(() => (rawCol && secKey && rawCol.section_goals?.[secKey] ? { ...rawCol, ...rawCol.section_goals[secKey] } : rawCol), [rawCol, secKey]);
  const canEdit = ctx.hasArea("finance", "settings") && ctx.feat("conference_goals");
  const [tick, setTick] = useState(0);
  const [editPay, setEditPay] = useState(null);
  const pp = ctx.settings?.permissions || {};
  const recAdmin = ctx.isAdmin && ctx.hasArea("records");
  const perm = { edit: ctx.isFullAdmin || (recAdmin && ctx.canEditRec && pp.admin_can_edit_conf_payments !== false), del: ctx.isFullAdmin || (recAdmin && pp.admin_can_delete_conf_payments !== false) };
  const deletePay = async (p) => {
    if (!window.confirm(`Delete this ${money(p.amount)} payment from ${p.church_name}?`)) { return; }
    try { await api.delete(`/donations/${p.id}`); toast.success("Payment deleted"); setTick((t) => t + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { if (filters.collection) { setName(filters.collection); } }, [filters.collection]);
  useEffect(() => {
    if (!name) return undefined;
    let live = true;
    api.get("/conference-summary", { params: { collection: name, destination: section || undefined, church_id: filters.church_id || undefined } }).then((r) => { if (live) { setData(r.data); } }).catch((e) => toast.error(errMsg(e)));
    return () => { live = false; };
  }, [name, tick, section, filters.church_id]);
  const saveGoals = useCallback(async (body, msg) => {
    try { await api.put(`/collections/${col.id}/goals`, body, { params: secKey ? { sec: secKey } : {} }); toast.success(msg); ctx.reload(); } catch (e) { toast.error(errMsg(e)); }
  }, [col, ctx, secKey]);
  const shown = useMemo(() => {
    const d = filters.category ? { ...data, donations: data.donations.filter((x) => x.category === filters.category) } : data;
    if (only === "donations") return { ...d, registrations: [], expenses: [] };
    if (only === "registrations") return { ...d, donations: [], expenses: [] };
    if (only !== "all") return { ...d, donations: [], registrations: [], expenses: [] };
    return d;
  }, [data, filters.category, only]);
  const m = useMemo(() => buildConference({ regions: ctx.regions, churches: filters.church_id ? ctx.churches.filter((c) => c.id === filters.church_id) : ctx.churches, collection: col, data: { ...shown, registrations: shown.registrations.filter((r) => r.paid !== false) } }), [ctx.regions, ctx.churches, col, shown, filters.church_id]);
  const sections = useMemo(() => [...conferenceSections(m, detail), ...(shown.registrations.length ? [{ ...eventTotals(shown.registrations), title: "Registration fees expected vs paid" }] : [])], [m, detail, shown]);
  const title = `${section || "MWJK"} Conference Summary - ${name}`;
  const subtitle = `${showing ? `${showing} - ` : ""}${detail ? "Full Detail" : "Summary Only"} - Printed ${fmtDate(new Date().toISOString())}`;
  const file = `${(section || "MWJK").replace(/\s+/g, "-")}-Conference-Summary-${name.replace(/\s+/g, "-")}`;

  if (!cols.length) return <p className="mt-6 rounded-xl border border-dashed bg-white p-10 text-center text-slate-500" data-testid="conf-summary-empty">No collections yet. Add one in Admin Panel → Categories & Lists → Collections.</p>;
  return (
    <div className="mt-6 space-y-6" data-testid="conf-summary">
      <div className="no-print flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="w-full sm:w-64"><div className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">Conference / collection</div>
          <NativeSelect value={name} onChange={setName} options={cols.map((c) => c.name)} testid="conf-summary-collection" />
        </div>
        <DefaultGoal col={col} canEdit={canEdit} onSave={(g) => saveGoals({ default_goal: g }, "Default goal saved")} />
        <div className="flex-1" />
        <ViewTabs detail={detail} setDetail={setDetail} />
        <Button variant="outline" onClick={() => exportPDF(`${file}.pdf`, title, subtitle, sections, sig)} data-testid="conf-summary-pdf"><FileDown className="mr-2 h-4 w-4" /> PDF</Button>
        <Button onClick={() => exportXLSX(`${file}.xlsx`, withShowing(sections, showing), sig)} data-testid="conf-summary-xlsx"><SheetIcon className="mr-2 h-4 w-4" /> Excel</Button>
      </div>
      <div className="annual-doc rounded-xl bg-white p-6 text-slate-900" data-testid="conf-summary-doc">
        <h2 className="font-heading text-2xl font-extrabold" data-testid="conf-summary-title">{name} — Conference Summary</h2>
        {showing && <p className="text-sm font-bold text-slate-900" data-testid="conf-summary-showing">{showing}{["pastor", "church", "progress", "tithes", "other"].includes(only) ? " — not part of the Conference Summary" : ""}</p>}
        <p className="text-sm text-slate-600">{detail ? "Full Detail" : "Summary Only"} · Churches grouped by region</p>
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label="Goal" value={money(m.t.goal)} testid="conf-summary-goal" />
          <Stat label="Paid by churches" value={money(m.t.paid)} testid="conf-summary-paid" accent="ring-2 ring-emerald-500" />
          <Stat label="Remaining balance" value={money(m.t.remaining)} testid="conf-summary-remaining" accent="ring-2 ring-[hsl(var(--gold))]" />
          <Stat label="Total income" value={money(m.t.income)} testid="conf-summary-total-income" />
          <Stat label="Balance" value={money(m.t.balance)} testid="conf-summary-total-balance" />
        </div>
        <div className="mt-6 space-y-8">
          {detail ? <PaymentsTable m={m} canEdit={canEdit} perm={perm} onEditPay={setEditPay} onDeletePay={deletePay} onGoal={(cid, g) => saveGoals({ church_id: cid, goal: g }, "Church goal saved")} /> : <ReportTable section={sections[0]} testid="conf-summary-regions" />}
          {detail && <ReportTable section={sections[1]} testid="conf-summary-registrations" />}
          {sections.slice(detail ? 2 : 1).map((s) => <ReportTable key={s.title} section={s} testid={`conf-summary-${s.title.toLowerCase()}`} />)}
        </div>
        <SignatureBlock sig={sig} className="px-0" />
      </div>
      {editPay && <RecordDialog cfg={RECORD_CONFIGS.donations} open record={editPay} onClose={() => setEditPay(null)} onSaved={() => { setEditPay(null); setTick((t) => t + 1); }} />}
    </div>
  );
}
